import DialogContentText from "@material-ui/core/DialogContentText";
import ActionDialog from "@saleor/components/ActionDialog";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import { DEFAULT_INITIAL_PAGINATION_DATA } from "@saleor/config";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import useUser from "@saleor/hooks/useUser";
import FulfillmentPageDialogs from "@saleor/masterFulfillment/components/FulfillmentPageDialogs";
import { getStringOrPlaceholder, maybe } from "@saleor/misc";
import OrderGenerateShippingLabel from "@saleor/motherOrders/components/OrderGenerateShippingLabel";
import OrderListPage from "@saleor/motherOrders/components/OrderListPage";
import { OrderDraftCreate } from "@saleor/motherOrders/types/OrderDraftCreate";
import { OrderCourierEnum } from "@saleor/motherOrders/utils/enums";
import { InvalidThreeMonth } from "@saleor/motherOrders/utils/handlers";
import { ListViews } from "@saleor/types";
import {
  ExportScope,
  FileTypesEnum,
  JobStatusEnum,
  OrderDirection,
  OrderSortField,
  SmopOrderStatusEnum,
  SmopUserTypeCode
} from "@saleor/types/globalTypes";
import { getGteLteVariables } from "@saleor/utils/filters";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import { getSortParams } from "@saleor/utils/sort";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router-dom";

import { getFilterVariables as getStoreOrderFilterVariables } from "../../../storeOrders/views/StoreOrderList/filters";
import {
  useOrderDraftCreateMutation,
  useRefreshGrabExpressStatus,
  useRefreshPandaGoAutoStatus,
  useStoreOrderExport,
  useUpdateShippingLabel
} from "../../mutations";
import {
  useDownloadTotalCount,
  useExportFile,
  useOrderDetailsQuery,
  useOrderListQuery
} from "../../queries";
import {
  orderListUrl,
  OrderListUrlDialog,
  OrderListUrlQueryParams,
  orderUrl
} from "../../urls";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";
import {
  getDefaultTab,
  getMoment,
  getUrlParam,
  hasAppliedFilters
} from "./handlers";
import { useUpdatePrint } from "./printHandlers";
import { getSortQueryVariables } from "./sort";

interface OrderListProps {
  params: OrderListUrlQueryParams;
}

export const OrderList: React.FC<OrderListProps> = ({ params }) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const paginate = usePaginator();
  const shop = useShop();
  const { updateListSettings, settings } = useListSettings(
    ListViews.ORDER_LIST
  );
  const intl = useIntl();
  const location = useLocation();
  const user = useUser();
  const isOpsAdmin = user.user?.userType.code === SmopUserTypeCode.OMI_OPS;
  const today = getMoment("YYYY-MM-DD");
  const month = getMoment("YYYY-MM-DD", -31);

  const [exportFileID, setExportFileID] = useState<string | undefined>();
  const [dialogButtonDisabled, setDialogButtonDisabled] = useState<boolean>(
    false
  );
  const [downloadStatus, setDownloadStatus] = useState<
    ConfirmButtonTransitionState
  >("default");
  const [refetchUnassignedRiders, setRefetchUnassignedRiders] = useState(false);

  useEffect(
    () =>
      navigate(
        orderListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );

  const handleCreateOrderCreateSuccess = (data: OrderDraftCreate) => {
    notify({
      status: "success",
      text: intl.formatMessage({
        defaultMessage: "Order draft successfully created"
      })
    });
    navigate(orderUrl(data.draftOrderCreate.order.id));
  };

  const [createOrder] = useOrderDraftCreateMutation({
    onCompleted: handleCreateOrderCreateSuccess
  });

  const createDefaultTabs = getDefaultTab(getFilterTabs);

  const tabs = createDefaultTabs;
  const DEFAULT_TAB_COUNT = 10;

  const getTabIndex = (
    { query, status }: OrderListUrlQueryParams,
    tabs: any[]
  ) => {
    // If there are no custom search queries, set the currentTab according to the index of the status in createDefaultTabs array
    if (query === "" || query === undefined) {
      return tabs.findIndex(t => t.data.status === status);
    }

    // If there is a search term or any filter, current tab is very last index plus 1 (added custom filter tab)
    return tabs.length + 1;
  };

  const currentTab = isOpsAdmin
    ? params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0)
    : params.activeTab === undefined
    ? areFiltersApplied(params)
      ? getTabIndex(params, tabs)
      : tabs.length // Else, current tab is "All" tab
    : parseInt(params.activeTab, 0);

  const [changeFilters, resetFilters] = createFilterHandlers({
    createUrl: orderListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    OrderListUrlDialog,
    OrderListUrlQueryParams
  >(navigate, orderListUrl, params);

  const handleTabChange = (tab: number) =>
    navigate(
      orderListUrl({
        activeTab: tab.toString(),
        ...createDefaultTabs[isOpsAdmin ? tab - 1 : tab].data
      })
    );

  const handleFilterTabDelete = () => {
    const currentCustomFilterTabIndex = currentTab - DEFAULT_TAB_COUNT;
    deleteFilterTab(currentCustomFilterTabIndex + 1); // +1 is to offset array index (workaround for splicing of orderFilter item in localStorage)
    navigate(orderListUrl());
  };

  const handleFilterTabSave = (data: SaveFilterTabDialogFormData) => {
    const searchQuery = getActiveFilters(params).query || "";
    saveFilterTab(data.name, getActiveFilters(params));
    navigate(
      orderListUrl({
        activeTab: tabs.length.toString(),
        query: searchQuery
      })
    );
  };

  useEffect(() => {
    if (params.payment !== null) {
      params.after = null;
      params.before = null;
    }
  }, [params]);

  const paginationState = createPaginationState(settings.rowNumber, params);
  const currencySymbol = maybe(() => shop.defaultCurrency, "USD");

  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),
    [params, settings.rowNumber]
  );

  const checkParamsSort = () => {
    if (params.sort === "date" || params.sort === "number") {
      return getSortQueryVariables(params);
    } else {
      return {
        direction: OrderDirection.ASC,
        field: OrderSortField.CREATION_DATE
      };
    }
  };

  const { data: TotalCount } = useDownloadTotalCount({
    displayLoader: true,
    skip: false,
    variables: {
      filter: {
        ...getStoreOrderFilterVariables(params),
        created: getGteLteVariables({
          /* Removes default date if any filter is applied */
          gte:
            !getUrlParam("createdFrom") && getUrlParam("shippingFrom")
              ? null
              : hasAppliedFilters(params)
              ? getMoment("YYYY-MM-DD", -90)
              : params.createdFrom || month,
          lte:
            !getUrlParam("createdTo") && getUrlParam("shippingTo")
              ? null
              : hasAppliedFilters(params)
              ? today
              : params.createdTo || today
        }),
        riderName: {
          firstName: params.riderFirstName,
          lastName: params.riderLastName
        }
      }
    }
  });

  const { data: ExportFileData, refetch: refetchExportFile } = useExportFile({
    displayLoader: true,
    skip:
      exportFileID === null || exportFileID === undefined || exportFileID === ""
        ? true
        : false,
    variables: { id: exportFileID }
  });

  const { data, loading, refetch } = useOrderListQuery({
    displayLoader: true,
    variables: {
      ...queryVariables,
      filter: {
        ...getFilterVariables(params),
        created: getGteLteVariables({
          /* Removes default date if any filter is applied */
          gte:
            !getUrlParam("createdFrom") && getUrlParam("shippingFrom")
              ? null
              : hasAppliedFilters(params)
              ? getMoment("YYYY-MM-DD", -90)
              : params.createdFrom || month,
          lte:
            !getUrlParam("createdTo") && getUrlParam("shippingTo")
              ? null
              : hasAppliedFilters(params)
              ? today
              : params.createdTo || today
        })
      },

      sort: checkParamsSort()
    }
  });

  const { data: OrderData } = useOrderDetailsQuery({
    displayLoader: true,
    skip: !params.orderId,
    variables: { id: params.orderId }
  });

  const [updateShippingLabel] = useUpdateShippingLabel({
    onCompleted: data => {
      if (data.orderUpdateShippingLabel.orderErrors.length === 0) {
        notify({
          status: "success",
          text: `Shipping Label has been updated`
        });
      } else {
        notify({
          status: "error",
          text: `${data.orderUpdateShippingLabel.orderErrors[0].message}`
        });
      }
      refetch();
    },
    onError: errors => {
      const [error] = errors.graphQLErrors;
      notify({
        status: "error",
        text: error?.message
      });
    }
  });

  const handleSubmitShippingLabel = async (
    { awbNumber, dispatchNumber, specialNotes, courier },
    orderStatus: SmopOrderStatusEnum,
    hasChanged: boolean
  ) => {
    const isFinalStatus = [
      SmopOrderStatusEnum.SHIPPED,
      SmopOrderStatusEnum.COMPLETED,
      SmopOrderStatusEnum.CANCELED
    ].includes(orderStatus);

    if (isFinalStatus || !hasChanged) {
      useUpdatePrint(maybe(() => OrderData?.order));
      return;
    }

    const response = await updateShippingLabel({
      variables: {
        awbNo: awbNumber,
        courier,
        dispatchNo: dispatchNumber,
        id: params.orderId,
        specialNotes
      }
    });

    const updatedOrder = maybe(
      () => response?.data?.orderUpdateShippingLabel?.order
    );
    const hasErrors =
      response.data.orderUpdateShippingLabel.orderErrors.length > 0;

    if (!hasErrors) {
      useUpdatePrint(updatedOrder);
    }
  };

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.orders.pageInfo),
    paginationState,
    params
  );

  const handleSort = createSortHandler(navigate, orderListUrl, params);

  const handleConfirm = async () => {
    setDialogButtonDisabled(true);
    setExportFileID(null);
    setDownloadStatus("loading");

    const result = await exportStoreOrders({
      variables: {
        input: {
          fileName: `Express Delivery Report${getMoment("_YYYYMMDDHHmmss")}`,
          fileType: FileTypesEnum.XLSX,
          filter: {
            ...getStoreOrderFilterVariables(params),
            created: getGteLteVariables({
              /* Removes default date if any filter is applied */
              gte:
                !getUrlParam("createdFrom") && getUrlParam("shippingFrom")
                  ? null
                  : hasAppliedFilters(params)
                  ? getMoment("YYYY-MM-DD", -90)
                  : params.createdFrom || month,
              lte:
                !getUrlParam("createdTo") && getUrlParam("shippingTo")
                  ? null
                  : hasAppliedFilters(params)
                  ? today
                  : params.createdTo || today
            }),
            riderName: {
              firstName: params.riderFirstName,
              lastName: params.riderLastName
            }
          },
          scope: ExportScope.FILTER
        }
      }
    });

    return result.data.exportStoreOrders.errors;
  };

  const cancelExport = () => {
    closeModal();
    setDialogButtonDisabled(false);
    setDownloadStatus("default");
    setExportFileID(null);
  };

  useEffect(() => {
    if (ExportFileData?.exportFile !== undefined) {
      if (ExportFileData?.exportFile?.status === JobStatusEnum.SUCCESS) {
        window.open(
          ExportFileData?.exportFile?.url,
          "_blank",
          "noopener,noreferrer"
        );
        setDownloadStatus("success");
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Fulfillment successfully downloaded."
          })
        });
        closeModal();
        setDialogButtonDisabled(false);
      } else if (ExportFileData?.exportFile?.status === JobStatusEnum.FAILED) {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Download Failed"
          })
        });
        setDownloadStatus("error");
        closeModal();
        setDialogButtonDisabled(false);
      } else {
        setDownloadStatus("loading");
        refetchExportFile();
      }
    }
  }, [ExportFileData]);

  const defaultToNewOrders = ({
    pathname,
    search
  }: {
    pathname: string;
    search: string;
  }): void => {
    // Checks if clicked tab is "fulfillment" tab from drawer or typed route in address bar is "/mother-orders"
    if (pathname === "/mother-orders" && search === "" && !isOpsAdmin) {
      navigate(
        orderListUrl({
          activeTab: "0",
          status: SmopOrderStatusEnum.NEW_ORDER
        })
      );
    }
  };

  useEffect(() => {
    defaultToNewOrders(location);
  }, [location]);

  const handleSearch = (text: string) => {
    navigate(orderListUrl({ ...params, query: text }));
  };

  const [refreshGrabExpressStatus] = useRefreshGrabExpressStatus({
    onCompleted: data => {
      if (data.refreshGrabExpressStatus.orderErrors.length === 0) {
        refetch();
      } else {
        notify({
          status: "error",
          text: `${data.refreshGrabExpressStatus.orderErrors[0].message}`
        });
      }
    },
    onError: errors => {
      const [error] = errors.graphQLErrors;
      notify({
        status: "error",
        text: error?.message
      });
    }
  });

  const [pandaGoRefreshStatus] = useRefreshPandaGoAutoStatus({
    onCompleted: data => {
      if (data?.refreshPandagoAutoStatus?.orderErrors?.length === 0) {
        refetch();
      } else {
        notify({
          status: "error",
          text: `${data?.refreshPandagoAutoStatus?.orderErrors[0]?.message}`
        });
      }
    },
    onError: errors => {
      const [error] = errors?.graphQLErrors;
      notify({
        status: "error",
        text: error?.message
      });
    }
  });

  const [exportStoreOrders] = useStoreOrderExport({
    onCompleted: ({ exportStoreOrders }) => {
      if (exportStoreOrders?.errors?.length === 0) {
        setExportFileID(exportStoreOrders?.exportFile?.id);
      }
    },
    onError: errors => {
      const [error] = errors.graphQLErrors;
      setDialogButtonDisabled(false);
      setDownloadStatus("default");
      setExportFileID(null);
      notify({
        status: "error",
        text: error?.message
      });
    }
  });

  useEffect(() => {
    if (InvalidThreeMonth(params.createdFrom, params.createdTo)) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Unable to filter the report data. Please limit the date range to 3 months."
        })
      });
      navigate(orderListUrl());
    }
  }, [params.createdFrom, params.createdTo]);

  return (
    <>
      <OrderListPage
        refetchOrders={refetch}
        brands={null}
        currencySymbol={currencySymbol}
        settings={settings}
        currentTab={currentTab}
        disabled={loading}
        filterOpts={getFilterOpts(params)}
        orders={maybe(() => data?.orders.edges.map(edge => edge.node))}
        pageInfo={pageInfo}
        sort={getSortParams(params)}
        onAdd={createOrder}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        onRowClick={id => () => navigate(orderUrl(id))}
        onSort={handleSort}
        onSearchChange={handleSearch}
        onFilterChange={changeFilters}
        onTabSave={() => openModal("save-search")}
        onTabDelete={() => openModal("delete-search")}
        onTabChange={handleTabChange}
        initialSearch={params.query || ""}
        tabs={createDefaultTabs.map(tab => tab.name)}
        onAll={resetFilters}
        queryVariables={queryVariables}
        activeTab={params.activeTab}
        generateShippingDialog={orderId =>
          openModal("onClickGenerateShippingLabel", { orderId })
        }
        onAssignRider={(
          orderId,
          orderCode,
          mallId,
          isFromNotification = false
        ) => {
          openModal("onClickAssignRider", {
            isFromNotification: String(isFromNotification),
            mallId,
            orderCode,
            orderId
          });
        }}
        onAddRemarks={(
          orderId,
          orderCode,
          orderOldRemarks,
          orderOldRemarkId
        ) => {
          if (orderOldRemarkId) {
            openModal("onClickAddRemarks", {
              orderCode,
              orderId,
              orderOldRemarkId,
              orderOldRemarks
            });
          } else {
            openModal("onClickAddRemarks", { orderCode, orderId });
          }
        }}
        onOverrideStatus={(
          orderId,
          orderStatus: SmopOrderStatusEnum,
          newOrderStatus: SmopOrderStatusEnum,
          courier
        ) => {
          if (courier === OrderCourierEnum.GRAB_EXPRESS) {
            openModal("confirmGrabOverride", {
              courier,
              newOrderStatus,
              orderId,
              orderStatus
            });
          } else {
            openModal("onOverrideStatus", {
              courier,
              newOrderStatus,
              orderId,
              orderStatus
            });
          }
        }}
        onUpdateStatus={(
          orderId,
          orderStatus: SmopOrderStatusEnum,
          newOrderStatus: SmopOrderStatusEnum,
          courier
        ) => {
          if (
            courier === OrderCourierEnum.GRAB_EXPRESS &&
            params.newOrderStatus !== "COMPLETED"
          ) {
            openModal("confirmGrabOverride", {
              courier,
              newOrderStatus,
              orderId,
              orderStatus
            });
          } else {
            openModal("onClickSaveButton", {
              newOrderStatus,
              orderId,
              orderStatus
            });
          }
        }}
        onCancelStatus={(courier, orderId) =>
          openModal("cancel", { courier, orderId })
        }
        download={() => openModal("download")}
        onGrabStatusRefresh={orderId => {
          refreshGrabExpressStatus({
            variables: {
              id: orderId
            }
          });
          refetch();
        }}
        onPandaGoStatusRefresh={orderId => {
          pandaGoRefreshStatus({
            variables: {
              id: orderId
            }
          });
          refetch();
        }}
        closeModal={closeModal}
        refetchUnassignedRiders={refetchUnassignedRiders}
      />

      <ActionDialog
        open={params.action === "download"}
        onClose={cancelExport}
        confirmButtonState={downloadStatus}
        disabled={dialogButtonDisabled}
        onConfirm={handleConfirm}
        title={intl.formatMessage({
          defaultMessage: "Download Fulfillment Reports",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="You are about to download report with {total} rows. Do you want to continue?"
            values={{
              total: <strong>{TotalCount?.storeOrders?.totalCount}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleFilterTabSave}
      />
      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleFilterTabDelete}
        tabName={getStringOrPlaceholder(tabs[currentTab]?.name)}
      />
      <OrderGenerateShippingLabel
        open={params.action === "onClickGenerateShippingLabel"}
        onUpdateShippingLabel={(
          courier,
          awbNumber,
          dispatchNumber,
          specialNotes,
          orderStatus,
          hasChanged
        ) =>
          handleSubmitShippingLabel(
            { awbNumber, courier, dispatchNumber, specialNotes },
            orderStatus,
            hasChanged
          )
        }
        orderData={OrderData?.order}
        onClose={closeModal}
        type="ORDER_LIST_ACTION"
      />

      <FulfillmentPageDialogs
        params={params}
        refetch={refetch}
        openModal={openModal}
        closeModal={closeModal}
        refetchUnassignedRiders={refetchUnassignedRiders}
        setRefetchUnassignedRiders={setRefetchUnassignedRiders}
      />
    </>
  );
};

export default OrderList;
